import React, { useState, useEffect } from 'react';
import Table from '../../Components/Table';
import { CommodityColumns } from '../../ColumnsConfig/CommodityColumns';
import './../../App.css';
import { Box, Button, Chip, IconButton, InputBase, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CommodityFormDialog from '../../Modals/CommodityFormDialog';
import ConfirmationModal from '../../Common/ConfirmationModal';
import CommodityCodeService from '../../Services/CommodityCodeService';
import loaderGif from "../../Assests/loader.gif";
import { NotificationContainer, NotificationManager } from 'react-notifications';
import 'react-notifications/lib/notifications.css';

const CommodityCode = () => {
  const [searchValue, setSearchValue] = useState('');
  const [chipValue, setChipValue] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortColumn] = useState('');
  const [order, setSortOrder] = useState('asc');
  const [commodityCode, setCommodityCode] = useState('');
  const validSortFields = ['commodity_code','name', 'description'];

  const fetchCommodityCodeData = async (
    page = currentPage,
    size = pageSize,
    sortByField = sortBy,
    sortOrder = order,
    searchTerm  = commodityCode
  ) => {
    setIsLoading(true);
    try {
      const queryObj = {
        page,
        pageSize: size,
        sortBy: validSortFields.includes(sortByField) ? sortByField : 'name',
        order: sortOrder,
        searchTerm: searchTerm.trim() || "",
      };
  
      const response = await CommodityCodeService.fetchCommodityCode(
        queryObj.page,
        queryObj.pageSize,
        queryObj.sortBy,
        queryObj.order,
         queryObj.searchTerm,
      );
  
      const { data, totalRecords, totalPages } = response;
  
      if (Array.isArray(data)) {
        setRows(data.map(row => ({ ...row, id: row.commodity_code_id })));
        setTotalRecords(totalRecords);
        setTotalPages(totalPages);
      } else {
        throw new Error('Unexpected API response format.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.ErrorMessage || "Failed to fetch Commodity Code data.";
      console.error('Failed to fetch Commodity Code data:', errorMessage);
      NotificationManager.error(errorMessage, "Error", 5000);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchCommodityCodeData();
  }, [currentPage, pageSize]);

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    fetchCommodityCodeData(page, size, sortBy, order,'');
  };

  const handleSearchChange = e => setSearchValue(e.target.value);

  const handleSort = (field) => {
    let sortField = field;
  
    if (field === 'description') {
      sortField = 'name';
    }
    else if (field === 'commodity_code') {
      sortField = 'commodity_code';
    }
  
    if (!validSortFields.includes(sortField)) {
      console.warn(`Invalid sort field: "${sortField}"`);
      return;
    }
  
    const newOrder = sortBy === sortField && order === 'asc' ? 'desc' : 'asc';
    setSortColumn(sortField);
    setSortOrder(newOrder);
  
    fetchCommodityCodeData(currentPage, pageSize, sortField, newOrder);
  };
  
  const handleSearch = () => {
    const trimmedSearchTerm = commodityCode.trim(); 
    setChipValue(trimmedSearchTerm);
    fetchCommodityCodeData(1, pageSize, sortBy, order, trimmedSearchTerm); 
  };

  const handleChipDelete = async() => {
    setChipValue('');
    try{
      await fetchCommodityCodeData(1, pageSize, sortBy, order, '');
  
    }catch(error){
      console.error('Error fetching SCAC data:', error);
      //NotificationManager.error("Failed to fetch SCAC data.", "Error", 5000);
    }finally{
      setCommodityCode('');
    }
  };
  const handleDialogOpen = () => {
    setInitialData(null);
    setIsDialogOpen(true);
  };

  const handleDialogClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setIsDialogOpen(false);
    }
  };

  const handleSave = async (formData) => {
      try {
          const { commodity_code,name, description } = formData;
          const dataToSubmit = {commodity_code, name, description };
          const dataToSubmit1 = {name, description };


          if (formData.commodity_code_id) {
              await CommodityCodeService.updateCommodityCode(formData.commodity_code_id, dataToSubmit1);
              NotificationManager.success("Commodity Code Updated successfully.", "Success", 3000);

          } else {
              await CommodityCodeService.createCommodityCode(dataToSubmit);
              NotificationManager.success("Commodity Code saved successfully.", "Success", 3000);
          }

          fetchCommodityCodeData(); // Refresh the table data
          setIsDialogOpen(false);
      } catch (error) {
          const errorMessage = error.response?.data?.ErrorMessage || "Failed to save Commodity Code.";
          console.error('Error saving Commodity Code:', errorMessage);
          NotificationManager.error(errorMessage, "Error", 5000);
      }
  };

  const handleEdit = rowData => {
    setInitialData(rowData);
    setIsDialogOpen(true);
  };

  const handleDelete = rowData => {
    setRowToDelete(rowData);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await CommodityCodeService.deleteCommodityCode(rowToDelete.id);
      setRows(rows.filter(row => row.id !== rowToDelete.id));
      setIsDeleteModalOpen(false);
      NotificationManager.success('Commodity Code deleted successfully.', 'Success', 3000);
    } catch (error) {
      console.error('Error deleting Commodity Code:', error);
      NotificationManager.error('Failed to delete Commodity Code.', 'Error', 5000);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  return (
    <div className="px-5 my-1" style={{ position: 'relative' }}>
      <NotificationContainer />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center">
          <Paper component="form" className="search-container" onSubmit={(e) => e.preventDefault()}>
            <InputBase
              className="search-input"
              placeholder="Search Commodity Code"
              inputProps={{ 'aria-label': 'search Commodity Code' }}
              value={commodityCode}
              onChange={(e) => setCommodityCode(e.target.value)}
            />
            <IconButton type="button" aria-label="search" onClick={handleSearch}>
              <SearchIcon />
            </IconButton>
          </Paper>
          {chipValue && (
            <Chip
              label={chipValue}
              onDelete={handleChipDelete}
              deleteIcon={<CloseIcon />}
              className="chip-style"
            />
          )}
        </Box>
        <Button variant="contained" className="add-new-btn" onClick={handleDialogOpen}>
          Add New Commodity Code
        </Button>
      </Box>

      <Table
        rows={rows}
        columns={CommodityColumns(handleEdit, handleDelete)}
        className="table-header"
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        totalPages={totalPages}
        getRowId={row => row.id}
        onSort={handleSort}
      />

      {isLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor="rgba(255, 255, 255, 0.8)"
          zIndex={1}
        >
          <img src={loaderGif} alt="Loading..." width={50} height={50} />
        </Box>
      )}

      <CommodityFormDialog
        open={isDialogOpen}
        handleClose={handleDialogClose}
        handleSave={handleSave}
        initialData={initialData}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        message="Are you sure you want to delete this Commodity Code?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default CommodityCode;
